import { useState, useEffect, useCallback, useRef } from 'react';

/** Reload anyway if no `controllerchange` arrives (e.g. a page no worker controls). */
export const UPDATE_RELOAD_FALLBACK_MS = 4000;

/**
 * Activates the worker waiting NOW, not the one seen at page load: a later
 * deploy replaces it, and a message to a replaced worker is silently lost.
 */
async function activateWaitingWorker(seen: ServiceWorker | null): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  (registration?.waiting ?? seen)?.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * `autoApply` activates a waiting worker as soon as one is found (the page
 * then reloads): for screens with no session and so no unsaved work.
 * Without it, the caller shows a prompt and calls `update`.
 */
export function usePWAUpdate({ autoApply = false }: { autoApply?: boolean } = {}) {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const update = useCallback(() => {
    if (!('serviceWorker' in navigator)) return;
    setIsUpdating(true);
    void activateWaitingWorker(waitingWorker);
    fallbackTimer.current ??= setTimeout(
      () => window.location.reload(),
      UPDATE_RELOAD_FALLBACK_MS
    );
  }, [waitingWorker]);

  useEffect(() => {
    if (autoApply && waitingWorker) update();
  }, [autoApply, waitingWorker, update]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleControllerChange = () => {
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // A worker found while the app boots is already `installing` (its
    // `updatefound` fired before this effect ran), so it is tracked here too.
    const track = (worker: ServiceWorker) => {
      const report = () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          setWaitingWorker(worker);
          setNeedsUpdate(true);
        }
      };
      worker.addEventListener('statechange', report);
      report();
    };

    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
        setNeedsUpdate(true);
      }
      if (registration.installing) track(registration.installing);

      registration.addEventListener('updatefound', () => {
        if (registration.installing) track(registration.installing);
      });

      // A tab that stays open never navigates, so the browser never checks
      // for a new worker by itself.
      registration.update().catch(() => {});
    });

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    };
  }, []);

  const dismiss = useCallback(() => {
    setNeedsUpdate(false);
  }, []);

  return { needsUpdate, isUpdating, update, dismiss };
}
